import React, { useContext, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Snackbar, Alert, Grid, Card, Typography, CardContent, IconButton, Tooltip } from "@mui/material";
import { UserContext } from "../context/userContext";
import { UserTableColumns } from "../components/Data/DataTableColumns_User";

import UserListGrid from "../components/userListGrid";
import ResetPasswordDialog from "../dialogs/user/ResetPasswordDialog";
import EditUserWizard from "../dialogs/EditUserWizard";
import UserActions from "../components/UserActions";

import { GetUsers, UpdateUserPassword, DeleteUser } from "../configuration/AAConfig_Security";
import { EmptyUserFilter } from "../components/Data/DataUserFilters";
import RestartAlt from "@mui/icons-material/RestartAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import { adsBlueA } from "../utils/ADSColours";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";

interface UserType {
  id: string;
  firstName: string;
  lastName: string;
  auditName: string;
  email: string;
  organisation: string;
  active: boolean;
  rights?: string[];
  extraInformation?: any[];
}

interface UserFilterType {
  firstName: string;
  lastName: string;
  auditName: string;
  email: string;
  organisation: string[];
  active: string[];
}

const HomePage: React.FC = () => {
  const { currentToken, logoff } = useContext(UserContext);
  const theme = useTheme();

  const [errorText, setErrorText] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserType[] | null>(null);
  const [filteredData, setFilteredData] = useState<UserType[] | null>(null);
  const [userOrganisations, setUserOrganisations] = useState<string[]>([]);
  const [editedUserId, setEditedUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showResetPassword, setShowResetPassword] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [showEditUser, setShowEditUser] = useState<boolean>(false);
  const [editedUser, setEditedUser] = useState<UserType | null>(null);
  const [errorAlert, setErrorAlert] = useState<boolean>(false);
  const [successAlert, setSuccessAlert] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<UserFilterType>(EmptyUserFilter);
  const [dataSort, setDataSort] = useState<string | null>(null);
  const [removedUserName, setRemovedUserName] = useState<string | null>(null);

  const activeStates = [
    { text: "Active", state: true },
    { text: "Inactive", state: false },
  ];

  useEffect(() => {
    const GetUserData = async () => {
      if (isLoading && !userData) {
        const usersInfo = await GetUsers(currentToken, (err: string | null) => setErrorText(err));
        setUserData(usersInfo);
      }
    };

    if (isLoading && !errorText) {
      GetUserData();
    } else if (errorText) {
      logoff();
    }
  }, [currentToken, errorText, isLoading, logoff, userData]);

  useEffect(() => {
    if (editedUserId && userData) {
      const user = userData.find((user) => user.id === editedUserId);
      if (user) setRemovedUserName(user.auditName);
    }
  }, [editedUserId, userData]);

  useEffect(() => {
    if (!errorText) setIsLoading(!userData);

    if (userData) {
      const orgs = [...new Set(userData.map((item) => item.organisation))];
      setUserOrganisations(orgs);
      FilterTheData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const FilterTheData = () => {
    if (userFilter) {
      const filters = [
        {
          condition: (item: UserType) => item.firstName.toLowerCase().includes(userFilter.firstName.toLowerCase()),
          enabled: userFilter.firstName.length > 0,
        },
        {
          condition: (item: UserType) => item.lastName.toLowerCase().includes(userFilter.lastName.toLowerCase()),
          enabled: userFilter.lastName.length > 0,
        },
        {
          condition: (item: UserType) => item.auditName.toLowerCase().includes(userFilter.auditName.toLowerCase()),
          enabled: userFilter.auditName.length > 0,
        },
        {
          condition: (item: UserType) => item.email.toLowerCase().includes(userFilter.email.toLowerCase()),
          enabled: userFilter.email.length > 0,
        },
        {
          condition: (item: UserType) => userFilter.organisation.includes(item.organisation),
          enabled: userFilter.organisation.length > 0,
        },
        {
          condition: (item: UserType) =>
            activeStates
              .filter((element) => userFilter.active.includes(element.text))
              .map((obj) => obj.state)
              .includes(item.active),
          enabled: userFilter.active.length > 0,
        },
      ];

      const conditions = filters.filter((item) => item.enabled).map((item) => item.condition);

      if (userData) {
        setFilteredData(conditions.length > 0 ? userData.filter((item) => conditions.every((f) => f(item))) : userData);
      }
    } else {
      setFilteredData(userData);
    }
  };

  useEffect(() => {
    if (userFilter) FilterTheData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter]);

  const handleFilterChange = (filter: UserFilterType) => {
    setUserFilter(filter);
  };

  useEffect(() => {
    setShowEditUser(editedUser ? true : false);
  }, [editedUser]);

  const onClickResetPassword = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    setEditedUserId(id);
    setShowResetPassword(true);
  };

  const onClickDeleteUser = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    setEditedUserId(id);
    setShowDeleteConfirmation(true);
  };

  const ResetPasswordIcon = (params: { id: string }) => (
    <Tooltip title="Reset Password" arrow placement="top">
      <IconButton size="small" sx={{ left: "25%" }} onClick={(e) => onClickResetPassword(e, params.id)}>
        <RestartAlt />
      </IconButton>
    </Tooltip>
  );

  const DeleteUserIcon = (params: { id: string }) => (
    <Tooltip title="Delete User" arrow placement="top">
      <IconButton size="small" sx={{ left: "25%" }} onClick={(e) => onClickDeleteUser(e, params.id)}>
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );

  const userGridColumns = [
    ...UserTableColumns,
    {
      field: "Password",
      headerName: "Password",
      sortable: false,
      renderCell: ResetPasswordIcon,
      headerClassName: "data-grid-header",
    },
    {
      field: "Delete",
      headerName: "Delete User",
      sortable: false,
      renderCell: DeleteUserIcon,
      headerClassName: "data-grid-header",
    },
  ];

  const updatePassword = async (password: string) => {
    setErrorText(null);
    const errors = await UpdateUserPassword(currentToken, editedUserId!, password);
    if (!errors) {
      setSuccessMsg("Password Reset");
      setSuccessAlert(true);
      setShowResetPassword(false);
    } else {
      setErrorMsg(errors);
      setErrorAlert(true);
    }
  };

  const RemoveUser = async () => {
    setErrorText(null);
    const errors = await DeleteUser(currentToken, editedUserId!);
    setShowDeleteConfirmation(false);

    if (!errors) {
      setSuccessMsg("User Removed");
      setSuccessAlert(true);
      setUserData(null);
    } else {
      setErrorMsg(errors);
      setErrorAlert(true);
    }
  };

  const handleEditUser = (params: { row: UserType }) => {
    setEditedUser({ ...params.row });
  };

  const handleNewUserClick = () => {
    setEditedUser({
      id: "",
      firstName: "",
      lastName: "",
      auditName: "",
      email: "",
      organisation: "",
      active: true,
      rights: [],
      extraInformation: [],
    });
  };

  const renderForm = () => (
    <>
      <Snackbar open={errorAlert} autoHideDuration={4000} onClose={() => setErrorAlert(false)}>
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>

      <Snackbar open={successAlert} autoHideDuration={4000} onClose={() => setSuccessAlert(false)}>
        <Alert severity="success">{successMsg}</Alert>
      </Snackbar>
      <Grid
        container
        spacing={1}
        sx={{ paddingTop: theme.spacing(10) }}
        direction="column"
        justifyContent="space-between"
        alignItems="left"
      >
        <Grid item xs>
          {showResetPassword && (
            <ResetPasswordDialog
              closeDialogHandler={() => setShowResetPassword(false)}
              continueHandler={updatePassword}
              continueError={errorText}
              backHandler={undefined}
            />
          )}
        </Grid>
        <Grid item xs>
          {showEditUser && (
            <EditUserWizard
              userDetails={editedUser}
              closeDialogHandler={() => setEditedUser(null)}
              updateUserHandler={(response: any, errors: any) => {
                if (!errors) {
                  setUserData(null);
                  setSuccessMsg("User Updated");
                  setSuccessAlert(true);
                  setEditedUser(null);
                  setShowEditUser(false);
                } else {
                  setErrorMsg(errors);
                  setErrorAlert(true);
                }
              }}              
              continueError={errorText}
            />
          )}
        </Grid>
        <Grid item xs>
          {showDeleteConfirmation && (
            <ConfirmationDialog
              title={`Delete User ${removedUserName}`}
              message={`Are you sure you want to delete user ${removedUserName}?`}
              hideIcon
              OkClickHandler={RemoveUser}
              CancelClickHandler={() => setShowDeleteConfirmation(false)}
            />
          )}
        </Grid>
        <Grid item xs sx={{ ml: theme.spacing(12), mr: theme.spacing(12) }}>
          {errorText ? <Alert severity="error">{errorText}</Alert> : null}
          <Grid container spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h4" component="div" sx={{ paddingTop: theme.spacing(1), mb: theme.spacing(1) }}>
                User Management
              </Typography>
              <Typography variant="subtitle2" sx={{ pb: "8px" }}>
                This enables users to be created and managed for use with ADS Products. You can filter out users below.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{ width: "100%" }}
        spacing={1}
        direction="column"
        justifyContent="space-between"
        alignItems="left"
      >
        <Grid container justifyContent="center">
          <Card variant="outlined" sx={{ width: "100%", display: "flex", maxWidth: "90%", border: 0 }}>
            <CardContent sx={{ width: "100%" }}>
              <Typography variant="subtitle2" sx={{ color: adsBlueA }}>
                Filters
              </Typography>
              <UserActions
                filters={userFilter}
                organisations={userOrganisations}
                handleNewUserClick={handleNewUserClick}
                onFilterChange={handleFilterChange}
              />
              <UserListGrid
                columns={userGridColumns}
                rows={filteredData || []}
                height={"70vh"}
                onDoubleClick={(params: any) => handleEditUser(params)}
                sortColumn={dataSort}
                updateSortColumn={(sort: string) => setDataSort(sort)}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  const renderBasic = () => (
    <Grid
      container
      spacing={1}
      sx={{ paddingTop: theme.spacing(50) }}
      direction="column"
      justifyContent="space-between"
      alignItems="left"
    >
      <Grid item>
        <h1>Loading data, please wait...</h1>
      </Grid>
    </Grid>
  );

  const renderPage = () => (!isLoading && currentToken ? renderForm() : renderBasic());

  return renderPage();
};

export default HomePage;
